/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import { Button, Dropdown, Flex, Modal, notification } from 'antd';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  CheckCircleOutlinedIcon,
  CloseCircleOutlinedIcon,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  EyeOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteCatalog } from 'services';
import type { CatalogSummary } from 'types';
import {
  adjustPageNumber,
  generateExternalPathname,
  generateTableDataSource,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    CATALOGS: { UPDATE_URL: CATALOGS_UPDATE_URL },
    CATALOG: { BASE_URL: CATALOG_BASE_URL },
  },
} = PATHNAME;

type CatalogsTableProps = {
  loading: boolean;
  total: number;
  catalogs: CatalogSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const CatalogsTable = React.memo<CatalogsTableProps>(
  ({
    loading,
    total,
    catalogs,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteCatalog = useLockFn(async ({ id }: CatalogSummary) => {
      deleteCatalog({ id })
        .then(() => {
          handleRefreshData();
          notification.success({
            message: 'کاتالوگ مورد نظر با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        });
    });

    const catalogsDataSource = generateTableDataSource(catalogs, 'id');

    return (
      <PrimitiveTable
        scroll={{ x: 300, y: 700 }}
        columns={[
          {
            title: 'برند',
            dataIndex: 'brand',
            key: 'brand',
            ellipsis: true,
            render: (_, { id, brand }) => {
              if (!brand) {
                return <span>فاقد برند</span>;
              }

              return (
                <AntdRouterLink
                  to={`${CATALOGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                  className='text-wrap'
                >
                  {brand.title}
                </AntdRouterLink>
              );
            },
          },
          {
            title: 'نمایه‌گذاری',
            dataIndex: 'seoIndex',
            key: 'seoIndex',
            width: 100,
            ellipsis: true,
            render: (_, { seo }) => {
              const { isIndex } = seo ?? {};
              return (
                <Flex justify='center'>
                  {isIndex ? (
                    <CheckCircleOutlinedIcon className='text-green-500' />
                  ) : (
                    <CloseCircleOutlinedIcon className='text-red-500' />
                  )}
                </Flex>
              );
            },
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id, brand } = record;
              const { slug = '' } = brand ?? {};

              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'see-catalog',
                          label: (
                            <AntdRouterExternalLink
                              href={generateExternalPathname(
                                CATALOG_BASE_URL,
                                slug,
                              )}
                              className='inline-block py-1 text-inherit'
                            >
                              <EyeOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                مشاهده در فروشگاه
                              </span>
                            </AntdRouterExternalLink>
                          ),
                        },
                        {
                          key: 'update-catalog',
                          label: (
                            <AntdRouterLink
                              to={`${CATALOGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش کاتالوگ
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-catalog',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف کاتالوگ مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteCatalog(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف کاتالوگ</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={catalogsDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
CatalogsTable.displayName = 'CatalogsTable';

export { CatalogsTable };
